import { motion } from 'framer-motion';
import { StarField } from './StarField';
import { ConstellationMark } from './ConstellationMark';
import { LINKS } from '../config';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
  }),
};

export function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pb-28 pt-40 md:pt-48">
      <div className="absolute inset-0 bg-aurora" />
      <StarField />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-midnight-950" />

      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div
            initial="hidden"
            animate="show"
            custom={0}
            variants={fadeUp}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-mist"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-teal shadow-glow" />
            Audit-pending · built in the open for Midnight
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            custom={0.1}
            variants={fadeUp}
            className="text-balance text-[2.75rem] leading-[1.08] sm:text-6xl"
          >
            A quiet wallet for a{' '}
            <span className="italic text-teal-soft">Midnight</span> network.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            custom={0.22}
            variants={fadeUp}
            className="mt-6 max-w-lg text-balance text-lg leading-relaxed text-mist"
          >
            Vela Wallet keeps your keys encrypted on your device, asks before every
            connection, and never phones home with your seed. One recovery phrase,
            unlimited named accounts, full control.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            custom={0.34}
            variants={fadeUp}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              href={LINKS.chromeWebStore}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-teal px-6 py-3.5 font-medium text-midnight-950 shadow-glow transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <ChromeGlyph />
              Add Vela to Chrome
            </a>
            <a
              href="#security"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-3.5 font-medium text-star transition-colors hover:border-white/25 hover:bg-white/[0.03]"
            >
              Read the security model
            </a>
          </motion.div>

          <motion.p
            initial="hidden"
            animate="show"
            custom={0.44}
            variants={fadeUp}
            className="mt-5 text-xs uppercase tracking-[0.14em] text-mist/70"
          >
            Free · Open build · Chromium-based browsers
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="relative aspect-square rounded-[2rem] border border-white/[0.06] bg-midnight-900/60 p-6 shadow-card backdrop-blur-sm">
            <ConstellationMark />
          </div>

          <FloatingChip
            className="-left-4 top-8 sm:-left-8"
            label="AES-GCM encrypted vault"
            delay={1.1}
          />
          <FloatingChip
            className="-right-2 bottom-10 sm:-right-6"
            label="Locked by default"
            delay={1.35}
          />
        </motion.div>
      </div>
    </section>
  );
}

function FloatingChip({
  label,
  className,
  delay,
}: {
  label: string;
  className: string;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className={`absolute hidden items-center gap-2 rounded-xl border border-white/10 bg-midnight-900/90 px-3.5 py-2.5 text-xs text-star shadow-card backdrop-blur-md sm:flex animate-[chip-float_7s_ease-in-out_infinite] ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-teal-soft" />
      {label}
    </motion.div>
  );
}

function ChromeGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10.25" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3.75" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 1.75V8.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M4.2 17.5L9.9 14.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M19.8 17.5L14.1 14.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
